import type { CSSProperties } from 'react';
import type React from 'react';
import { useEffect, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives';

import { ContentBlock } from '@/libraries/design/components/content-block/src/content-block';
import { wacToken } from '@/libraries/design/foundations';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';

import { getOverageRateTiers } from '../calculator/overage-cost';
import { METER_CONTENT } from '../content/meter-content';
import { useGoToNextStep, useResetAll, useSelector } from '../context/hooks';
import {
	getCurrencyIsoCode,
	getNumberFormatLocale,
	selectMeter,
	selectTotalMonthlyCost,
	selectTotalOverageCost,
} from '../context/selectors';
import {
	useUbpStep3BackClickedEvent,
	useUbpStep3CompareNowClickedEvent,
	useUbpStep3FeedbackClickedEvent,
	useUbpStep3ScreenViewedEvent,
	useUbpStep3ShareClickedEvent,
} from '../ubp-calculator-analytics';

import { EstimateDisclaimer } from './estimate-disclaimer';
import { FeedbackButtons, type FeedbackVote } from './feedback-buttons';
import { HowYourTeamsMightUse } from './how-your-teams-might-use';
import { OrgNeedsResultsCard } from './org-needs-results-card';
import { ResultsCard } from './results-card';
import { ShareButton } from './share-button';
import { Stepper } from './stepper';
import { formatPrice, formatPriceRange } from './strings';

/**
 * Extra ResultsCard rendered between OrgNeedsCard and
 * HowYourTeamsMightUse — only for meters whose content bundle defines
 * `estimateStep.extraResultsCard` (currently AI agent resolutions per
 * Figma node 1160:20784). Uses the same ResultsCard primitive as the
 * main OrgNeedsCard for visual consistency.
 */
const ExtraResultsCardSlot = () => {
	const meter = useSelector(selectMeter);
	const extra = METER_CONTENT[meter].estimateStep.extraResultsCard;
	// Compute total monthly cost across the currently-selected products
	// using Briet's calculator (`selectTotalMonthlyCost` sums per-unit ×
	// unitCount using the CCP pricing data). Only displayed when the
	// meter's extra card is the "Monthly Cost Estimate" variant (CSR).
	// Falls back to the spec's static headline when the calculation is
	// pending or products aren't yet selected.
	const totalCost = useSelector(selectTotalMonthlyCost);
	const currency = useSelector(getCurrencyIsoCode);
	const locale = useSelector(getNumberFormatLocale);
	if (!extra) {
		return null;
	}
	const isMonthlyCostVariant = extra.eyebrow?.toUpperCase().includes('COST');
	// `extra.headline` is optional in the content type: meters may omit it.
	// If the meter's content omits it AND we don't have a real
	// live-price to compute (either not the cost-variant or CCP pricing
	// still loading), fall back to an empty string so the required
	// ResultsCard headline prop stays a string.
	const headline =
		isMonthlyCostVariant && totalCost !== undefined
			? formatPrice(totalCost, { currency, locale })
			: (extra.headline ?? '');
	return (
		<ResultsCard
			eyebrow={extra.eyebrow}
			headline={headline}
			headlineSubtext={extra.headlineSubtext}
		/>
	);
};

/**
 * Monthly Cost Estimate card — Estimate step (Figma node 2164:27044).
 *
 * Presentational twin of the Summary step's overage-cost card. Renders
 * whenever the current meter defines `summaryStep.overageCostCard`. The
 * card's body copy is dynamic based on whether the org's estimated
 * usage exceeds its included allowance:
 *
 *   - overage case  → `body`         ("...not 100% covered by allowances.")
 *   - covered case  → `bodyCovered`  ("...usage is covered by allowances.")
 *
 * `{rate}` / `{rate2}` tokens in `rateNote` are substituted with
 * currency-formatted per-unit overage rates from the pricing config at
 * render time.
 *
 * Reuses `summaryStep.overageCostCard` (rather than duplicating the copy
 * onto `estimateStep`) so a single source of truth in each meter's
 * content bundle drives both steps. Note: Step 3 intentionally omits the
 * card's `listItems` bullets — they render only on the Step 5 Summary
 * card (SummaryOverageCostCard).
 */
const MonthlyCostEstimateCard = () => {
	const meter = useSelector(selectMeter);
	const overageCostCard = METER_CONTENT[meter].summaryStep.overageCostCard;
	const overageCost = useSelector(selectTotalOverageCost);
	const currency = useSelector(getCurrencyIsoCode);
	const locale = useSelector(getNumberFormatLocale);
	// Per revised design (2026-07-16): the Monthly Cost Estimate card on
	// Step 3 (Estimate) renders for CSR only. Assets shows the card on
	// Step 5 (Summary) but NOT on Step 3. The `overageCostCard` field
	// itself is still defined in both CSR + Assets content files because
	// Step 5's SummaryOverageCostCard consumes it there.
	if (!overageCostCard || meter !== 'csResolutions') {
		return null;
	}
	const overageRangeText = formatPriceRange(overageCost.low, overageCost.high, {
		currency,
		locale,
	});
	// Substitute `{rate}` / `{rate2}` … tokens in the content-registry
	// `rateNote` with the currency-formatted per-unit rate(s) from the
	// pricing config. Sub-dollar rates show cents so "$0.50" doesn't
	// round to "$1".
	const overageRateNote = getOverageRateTiers(meter).reduce(
		(note, tier, index) =>
			note.replace(
				index === 0 ? '{rate}' : `{rate${index + 1}}`,
				`${currency} ${formatPrice(tier.price, {
					currency,
					locale,
					fractionDigits: Number.isInteger(tier.price) ? 0 : 2,
				})}`,
			),
		overageCostCard.rateNote,
	);
	return (
		<ResultsCard
			eyebrow={overageCostCard.eyebrow}
			headline={overageRangeText}
			headlineSubtext={overageRateNote}
			testId="estimate-monthly-cost"
		/>
	);
};

export const ExampleUsage = ({ onCompareNow }: { onCompareNow?: () => void }) => {
	const intl = useIntl();
	const meter = useSelector(selectMeter);
	const goToNext = useGoToNextStep();

	// Meter-aware Compare CTA — sourced from the content registry so it
	// tracks the per-meter Confluence spec's "Card 1 Primary CTA" row.
	const compareCta = intl.formatMessage({
		id: `ubp.calculator.estimate.${meter}.compareCta`,
		defaultMessage: METER_CONTENT[meter].estimateStep.compareCta,
	});
	// Optional external href for the Compare CTA. When present the
	// button navigates to that URL (currently AI agent resolutions →
	// /collections/service). When absent, the button falls back to
	// advancing to the next calculator step.
	const compareHref = METER_CONTENT[meter].estimateStep.compareHref;
	// Meter-aware ContentBlock copy (title, intro, list items). Each is
	// wrapped in intl.formatMessage with a per-meter message ID so
	// translators can localize per-meter without disturbing others.
	const contentBlockTitle = intl.formatMessage({
		id: `ubp.calculator.estimate.${meter}.contentBlock.title`,
		defaultMessage: METER_CONTENT[meter].estimateStep.contentBlock.title,
	});
	const contentBlockIntro = intl.formatMessage({
		id: `ubp.calculator.estimate.${meter}.contentBlock.intro`,
		defaultMessage: METER_CONTENT[meter].estimateStep.contentBlock.intro,
	});
	const contentBlockListItems = METER_CONTENT[meter].estimateStep.contentBlock.listItems.map(
		(item, idx) =>
			intl.formatMessage({
				id: `ubp.calculator.estimate.${meter}.contentBlock.listItems.${idx}`,
				defaultMessage: item,
			}),
	);

	// Uses the design-library ContentBlock so the section matches Figma
	// node 1103:32363 ("Content Blocks Vertical") and inherits the WAC
	// design-system typography, spacing, and image treatment (16:9,
	// rounded corners). Do NOT swap this for a wpl/ component — see the
	// "Component Sourcing Rule" in wac/AGENTS.md.
	//
	return (
		<ContentBlock
			imageBackgroundColor={METER_CONTENT[meter].estimateStep.image.imageBackgroundColor}
			title={contentBlockTitle}
			titleAs="h3"
			titleAppearsAs="card-title"
			// Structured content per Figma node 1103:32363: intro paragraph
			// + a small bulleted list. Passed as a single React element (a
			// `<div>`) so it satisfies ContentBlock's `text: string |
			// ReactElement` prop shape. The ContentBlock renders elements
			// as-is (no `body-lg`/`body-md` wrapping), so the caller uses
			// `body-md` classes to keep the typography consistent with the
			// default string rendering.
			text={
				// Body copy must render at "body regular" (16/24) even on
				// wide viewports. The `p, .body-md` rule under WAC-refresh
				// typographyStylesRefresh (see
				// `libraries/wpl/foundations/src/typography-styles.tsx`
				// line 474-479) bumps every `<p>` to 18/26 at `above.lg`,
				// which visually reads as "body large" and doesn't match
				// the ContentBlock Figma spec for this section. To keep
				// the copy at body-regular:
				//   1. Use a `<div>` (not `<p>`) so the responsive `p`
				//      selector doesn't apply.
				//   2. Explicitly declare the body-md font-size /
				//      line-height on both the intro block and the bullet
				//      list to lock in 16/24 across breakpoints.
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '12px',
						fontFamily: wacToken('font.charlie.text'),
						fontSize: '1rem',
						lineHeight: '1.5rem',
						fontWeight: 400,
					}}
				>
					<div>{contentBlockIntro}</div>
					<ul
						style={{
							margin: 0,
							paddingInlineStart: '1.25rem',
							display: 'flex',
							flexDirection: 'column',
							gap: '4px',
							fontSize: '1rem',
							lineHeight: '1.5rem',
							fontWeight: 400,
						}}
					>
						{contentBlockListItems.map((item, i) => (
							// Meter-aware bulleted list — items come from the
							// atomic content registry.
							<li key={`cb-li-${i}`}>{item}</li>
						))}
					</ul>
				</div>
			}
			alignment="start"
			orientation="vertical"
			imageAspectRatio="16 / 9"
			imageProps={{
				// Meter-aware image — sourced from the atomic per-meter
				// content registry (`src/content/*.ts`,
				// `estimateStep.image`). All four meters currently point
				// to the same Orange Logic asset while the content team
				// finalizes per-meter hero imagery; each file is
				// independently swappable to a per-meter URL without
				// touching this component. Track replacement in the
				// follow-up content ticket.
				src: METER_CONTENT[meter].estimateStep.image.src,
				alt: METER_CONTENT[meter].estimateStep.image.alt,
			}}
			// Small secondary button per Figma node 1103:32350. The
			// ContentBlock's default button size is derived from title
			// semantics (`h4` → small, else large). Since our title is
			// visually h3-sized, we opt into the compact `small` size
			// explicitly via the new `buttonSize` override.
			//
			// The CTA doubles as the Estimate step's "Next" action — clicking
			// it advances the calculator to the Summary step. This is why
			// the Estimate step's StepNav omits `showNext`: the ContentBlock
			// button IS the forward navigation for this step.
			buttonSize="small"
			callToActionProps={{
				buttonText: compareCta,
				buttonVariant: 'secondary',
				button: compareHref
					? {
							// External destination — render as a real anchor
							// so it opens in a new tab, is right-clickable,
							// and is bookmarkable. Still fire the analytics
							// event before default navigation.
							href: compareHref,
							onClick: () => onCompareNow?.(),
						}
					: {
							type: 'button',
							onClick: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
								e.preventDefault();
								onCompareNow?.();
								goToNext();
							},
						},
			}}
		/>
	);
};

export const EstimateStep = () => {
	const intl = useIntl();
	const meter = useSelector(selectMeter);
	const resetAll = useResetAll();
	const [vote, setVote] = useState<FeedbackVote>(null);

	const fireStep3Viewed = useUbpStep3ScreenViewedEvent();
	const fireStep3CompareNow = useUbpStep3CompareNowClickedEvent();
	// GWP-12674 — analytics event reused for the new Restart button.
	// Same underlying UI event ID / actionSubject; the semantic gesture
	// changed from "Back" to "Restart" but for analytics-continuity
	// purposes we keep the existing hook so historical funnels aren't
	// disrupted.
	const fireStep3Back = useUbpStep3BackClickedEvent();
	const fireStep3Share = useUbpStep3ShareClickedEvent();
	const fireStep3Feedback = useUbpStep3FeedbackClickedEvent();

	useEffect(() => {
		fireStep3Viewed();
		// Intentionally run once on mount only.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	// All meters (including AI agent resolutions / csResolutions) share
	// the same two-column Estimate step layout. Per-meter content is
	// resolved via the atomic content registry (`METER_CONTENT[meter]`)
	// so the same JSX renders correctly for every meter. Prior to
	// GWP-1XXXX a separate `<EstimateCsrStep>` branch existed with a
	// 3-column layout — it was removed once CSR gained proper registry
	// content.
	return (
		<>
			<Stepper visibleStepCount={3} />
			{/*
			 * Estimate step header per Figma node 1103:32350:
			 * - H3 (Charlie Display Bold 32/38) "Review usage estimate"
			 * - Body Large (Charlie Text Regular 20/28) subtitle
			 *
			 * Wrapped in TypographyStyles so raw `h3` inherits the WAC
			 * design-system Charlie font stack (same pattern used elsewhere
			 * in the calculator).
			 */}
			<TypographyStyles>
				<div style={pageHeaderStyle}>
					<h3 style={pageTitleStyle}>
						{intl.formatMessage({
							id: 'ubp.calculator.estimate.title',
							defaultMessage: 'Review usage estimate',
						})}
					</h3>
					{/*
					 * Meter-aware subtitle — sourced from the content registry
					 * so the byline reflects the customer-facing meter name
					 * (e.g. "rovo credits" vs "AI agent resolutions"). See
					 * `src/content/*.ts` for per-meter spec sources.
					 */}
					<p style={pageSubtitleStyle}>
						{intl.formatMessage({
							id: `ubp.calculator.estimate.${meter}.subtitle`,
							defaultMessage: METER_CONTENT[meter].estimateStep.subtitle,
						})}
					</p>
				</div>
			</TypographyStyles>
			<div style={twoColumnRowStyle}>
				<div style={twoColumnLeftStyle}>
					<OrgNeedsResultsCard testId="estimate-org-needs" />
					{/*
					 * Monthly Cost Estimate card (GWP-12277 / Figma nodes
					 * 2164:27044 & 2164:28256) — sits below OrgNeedsCard for
					 * every meter whose atomic content bundle defines
					 * `summaryStep.overageCostCard` (currently all four:
					 * Rovo, Automations, AI agent resolutions, Assets).
					 * Body copy dynamically switches between the covered
					 * and overage cases based on Briet's cost calculator.
					 */}
					<MonthlyCostEstimateCard />
					{/*
					 * Extra ResultsCard slot — only rendered for meters that
					 * define `estimateStep.extraResultsCard` in the atomic
					 * content registry (currently AI agent resolutions per
					 * Figma node 1160:20784). Absent for Rovo / Automations /
					 * Assets.
					 */}
					<ExtraResultsCardSlot />
					<HowYourTeamsMightUse />
				</div>
				<div style={twoColumnRightStyle}>
					<ExampleUsage onCompareNow={fireStep3CompareNow} />
				</div>
			</div>
			{/*
			 * Feedback buttons — sit between the content and the Back/Share
			 * row per Figma node 1103:32350.
			 */}
			<div css={feedbackWrapperStyle}>
				<FeedbackButtons
					vote={vote}
					onVote={(v) => {
						setVote(v);
						// Only fire the analytics event when the user actually
						// casts a vote. `v` can be null when they toggle their
						// current vote off, in which case there's nothing to
						// report.
						if (v !== null) {
							fireStep3Feedback(v);
						}
					}}
				/>
			</div>
			{/*
			 * Bottom action row per Figma node 1103:32350 (updated per
			 * GWP-12674 — Back button replaced with Restart):
			 *   [Restart]  [Copy URL to share]
			 *
			 * Both buttons sit side-by-side, vertically centered, left-aligned.
			 * No `<StepNav>` — its centered 3-slot layout doesn't match the
			 * Estimate step's simpler two-button row. The ContentBlock's
			 * "Compare now" button acts as the forward navigation.
			 *
			 * RESTART BUTTON DECISION (GWP-12674):
			 *   Replaces the previous Back link. Restart resets the entire
			 *   calculator flow to Step 1 (Usage type) with a completely
			 *   blank state — meter, usage type, org size, products, and
			 *   errors are all wiped. Users who reach the Estimate step
			 *   and want to explore a different meter shouldn't have to
			 *   step back one screen at a time.
			 *
			 *   Uses WAC design-library `<Link type="primary">` — the
			 *   Figma spec shows a blue-text anchor treatment identical
			 *   to the previous Back link, so we reuse that visual with
			 *   an `onClick`-driven state-reset instead of navigation.
			 *
			 * COPY URL: uses the WAC design-library `<Button variant="primary">`
			 * (blue filled) with a link icon, per Figma.
			 */}
			<div style={bottomActionRowStyle}>
				{/*
				 * Restart is a real <button> styled to match the Figma "medium
				 * anchor default" spec (blue text, no border, no background).
				 *
				 * Why NOT the WAC design-library `<Link>` component here:
				 *   The Restart action doesn't navigate — it resets calculator
				 *   state in-place. AFM's `@atlassian/a11y/anchor-is-valid`
				 *   rule is RATCHETED (cannot be disabled per-line) and will
				 *   flag any anchor with `href="#"` + `onClick` as
				 *   "anchor used as button". The correct semantic answer is a
				 *   `<button>` element. We inline the Link visual (color, size,
				 *   font, hover/focus) here rather than adding a new "text-only
				 *   button" variant to the design library.
				 */}
				<button
					type="button"
					css={restartButtonStyle}
					onClick={() => {
						fireStep3Back();
						resetAll();
					}}
				>
					Restart
				</button>
				<ShareButton onShare={fireStep3Share} />
			</div>
			<EstimateDisclaimer />
		</>
	);
};

export const panelStyle: CSSProperties = {
	padding: 16,
	border: `1px solid ${wacToken('color.neutral.light300')}`,
	borderRadius: 8,
	backgroundColor: wacToken('color.neutral.light100'),
	height: '100%',
	boxSizing: 'border-box',
	display: 'flex',
	flexDirection: 'column',
};

export const panelTitleStyle: CSSProperties = {
	margin: '0 0 12px',
	fontSize: 16,
	fontWeight: 700,
	color: wacToken('color.neutral.light1100'),
};

/**
 * Estimate step header / bottom-action-row styles.
 *
 * The old `stepTitleStyle` (a plain 22px bold heading) was removed —
 * the Estimate step now uses the design-system H3 + body-large pattern
 * (see `pageTitleStyle` / `pageSubtitleStyle` below), consistent with
 * the other calculator steps.
 */
const pageHeaderStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	gap: '8px',
	// 40px above the H3 (between the Stepper and the title) and 40px below
	// the subtitle (before the cards start), per Figma node 1103:32350.
	paddingBlock: '40px',
	maxWidth: '688px',
};

const pageTitleStyle: CSSProperties = {
	// TypographyStyles maps raw `h3` to Charlie Display Bold 32/38 —
	// matches Figma spec (node 1103:32354).
	margin: 0,
};

const pageSubtitleStyle: CSSProperties = {
	// Body Large: Charlie Text Regular 20/28, matches Figma node 1103:32357.
	margin: 0,
	fontSize: '20px',
	lineHeight: '28px',
	fontWeight: 400,
	color: wacToken('color.neutral.light1100'),
};

// Restart button — matches Figma "medium anchor default": blue text,
// no border, no background, Charlie Text Bold 16/24 with underline on
// hover/focus for affordance. Focus-visible ring for keyboard users.
// KEEP IN SYNC with the identical `restartButtonStyle` in
// `summary-step.tsx` (GWP-12674). Compiled CSS's `@compiled/no-exported-css`
// rule forbids exporting `css()` return values because the transformer
// needs to inline the style at build time in the file where it's used —
// so we duplicate rather than centralize. If you edit either copy, edit
// the other too.
const restartButtonStyle = css({
	appearance: 'none',
	background: 'transparent',
	border: 'none',
	padding: 0,
	margin: 0,
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	lineHeight: '24px',
	fontWeight: 400,
	color: wacToken('color.primary.blue'),
	cursor: 'pointer',
	textDecoration: 'none',
	'&:hover, &:focus-visible': {
		textDecoration: 'underline',
	},
	'&:focus-visible': {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '2px',
		borderRadius: '2px',
	},
});

const bottomActionRowStyle: CSSProperties = {
	display: 'flex',
	// Vertically center Back + Share on a single row per Figma.
	alignItems: 'center',
	// Horizontally centered per Figma — Back + Copy URL sit in the middle
	// of the calculator container, with a 12px gap between them (matches
	// the gap used inside StepNav for visual consistency).
	justifyContent: 'center',
	gap: '12px',
	paddingBlockStart: '40px',
};

// Two-column layout for the main Estimate step content sections.
// Uses `grid-template-columns: repeat(auto-fit, minmax(400px, 1fr))` so
// the grid intrinsically collapses to a single column below the WAC `sm`
// breakpoint (roughly viewports < 832px) without needing a media query.
// This keeps the layout styleable with plain inline styles.
// The `min(400px, 100%)` intrinsic width lets a single card collapse
// below 400px on narrow viewports (phones) rather than overflowing the
// container. Above 832px the grid still auto-fits into two 400px cols.
const twoColumnRowStyle: CSSProperties = {
	display: 'grid',
	gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))',
	gap: '32px',
	alignItems: 'start',
};

// Left column — stacks OrgNeedsCard on top of HowYourTeamsMightUse.
const twoColumnLeftStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	gap: '16px',
};

// Right column — holds the ExampleUsage ContentBlock. Explicit wrapper
// (rather than just placing ExampleUsage directly) keeps parallel styling
// hooks available if the right side needs its own gap/padding later.
const twoColumnRightStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
};

// Wrapper for the FeedbackButtons section — 56px above (40 + 16) and
// 44px below (40 + 4) per the design's spacing spec on top of the base
// 40px rhythm. Horizontally centered per Figma.
// On mobile, feedback aligns left (matches the surrounding content
// grid); on tablet/desktop, centered within the calculator. The
// FeedbackButtons component itself handles the row-vs-column layout;
// this wrapper just controls the outer horizontal placement.
// Feedback wrapper — 56px above (40 + 16) and 44px below (40 + 4) per
// design spacing spec.
//   Mobile (< 768px): left-align so prompt + thumbs sit at the same
//     x-origin as the surrounding content grid.
//   Desktop (>= 768px): centered per Figma.
const feedbackWrapperStyle = css({
	display: 'flex',
	justifyContent: 'flex-start',
	paddingBlockStart: '56px',
	paddingBlockEnd: '44px',
	[media.above.md]: {
		justifyContent: 'center',
	},
});

export const columnStackStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	gap: 16,
};

export const splitHalfRowStyle: CSSProperties = {
	display: 'grid',
	gridTemplateColumns: 'repeat(2, 1fr)',
	gap: 16,
	alignItems: 'stretch',
};
